import { Download, FileText, Landmark, Mail, Plus, Trash2, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { I18nextProvider, useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
  Badge,
  Button,
  Checkbox,
  EmptyState,
  ErrorState,
  IconButton,
  LoadingState,
  ModalBackdrop,
  Notice,
  ResponsiveTable,
  Select,
  TextArea,
  TextInput,
  dialogScrollBodyClass,
  dialogScrollFooterClass,
  dialogScrollPanelClass,
  mutedTextClass,
  type ResponsiveTableColumn,
} from '../../components/ui'
import type { BadgeTone } from '../../components/ui/kit'
import { errorMessage } from '../../lib/api'
import { businessTodayIso } from '../../lib/businessDate'
import { downloadFile } from '../../lib/download'
import { useDirectory } from '../../lib/hr'
import { masked } from '../../lib/me'
import { formatMoney } from '../../lib/money'
import {
  myOneOffPdfUrl,
  oneOffBankExport,
  oneOffPdfUrl,
  useCreateOneOff,
  useEditOneOff,
  useIssueOneOff,
  useMailOneOff,
  useMyOneOff,
  useMyOneOffs,
  useOneOffPreview,
  useOneOffs,
  useVoidOneOff,
  type MyOneOff,
  type OneOff,
  type OneOffDraft,
  type OneOffLine,
  type OneOffStatus,
  type OneOffWorking,
} from '../../lib/oneOff'
import { useMe } from '../../lib/queries'
import { downloadWorkbook } from '../../lib/roster/download'
import { can } from '../../lib/types'
import { useConfirm } from '../roster/ConfirmDialog'
import { bankSheet } from './bankFile'
import {
  MANUAL_KINDS,
  blankDraft,
  capShown,
  cleanDraft,
  draftOf,
  draftProblem,
  lineLabel,
  oneOffActions,
  percentText,
  sendNotice,
  voidMustReissue,
} from './oneOff'
import { defaultPayslipLang, type PayslipLang } from './payslipLang'
import { PayslipLangPicker } from './PayslipLangPicker'

const STATUS_TONE: Record<OneOffStatus, BadgeTone> = { Draft: 'warning', Issued: 'success', Void: 'muted' }

function monthText(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`
}

function Line({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
      <dd
        className={`text-right tabular-nums ${
          strong ? 'font-semibold text-slate-900 dark:text-slate-100' : 'text-slate-700 dark:text-slate-200'
        }`}
      >
        {value}
      </dd>
    </div>
  )
}

/** The working from gross to net, with an unset cap said as "no cap" and never as a zero. */
function OneOffWorkingView({ working }: { working: OneOffWorking }) {
  const { t } = useTranslation()
  const cap = capShown(working.social_cap)
  return (
    <div className="space-y-3 text-sm">
      <dl className="space-y-1">
        {working.lines.map((line, index) => (
          <Line key={index} label={lineLabel(line, t)} value={formatMoney(line.amount)} />
        ))}
        <Line label={t('oneOff.gross')} value={formatMoney(working.gross)} strong />
      </dl>
      <dl className="space-y-1 border-t border-border-subtle pt-3">
        <Line label={t('oneOff.socialCap')} value={cap.kind === 'none' ? t('oneOff.noCap') : formatMoney(cap.value)} />
        <Line label={t('oneOff.insuredBefore')} value={formatMoney(working.insured_before)} />
        {working.cap_left != null && <Line label={t('oneOff.capLeft')} value={formatMoney(working.cap_left)} />}
        <Line label={t('oneOff.socialBase')} value={formatMoney(working.social_base)} />
        <Line
          label={`${t('oneOff.social')} (${t('oneOff.socialHow', { code: working.soc_code, percent: percentText(working.soc_percent) })})`}
          value={working.social_exempt ? t('oneOff.exempt') : formatMoney(working.social)}
        />
        <Line label={t('oneOff.taxBase')} value={formatMoney(working.tax_base)} />
        <Line
          label={`${t('oneOff.tax')} (${percentText(working.tax_percent)})`}
          value={working.tax_exempt ? t('oneOff.exempt') : formatMoney(working.tax)}
        />
      </dl>
      <dl className="border-t border-border-subtle pt-3">
        <Line label={t('oneOff.net')} value={formatMoney(working.net)} strong />
      </dl>
      {working.warnings.map((code) => (
        <Notice key={code}>{t(`oneOff.warn.${code}`, { defaultValue: code })}</Notice>
      ))}
    </div>
  )
}

function PayoutTerms({ row }: { row: OneOff }) {
  const { t } = useTranslation()
  if (!row.payout_terms.length) return null
  return (
    <section className="space-y-2 rounded-lg border border-border-subtle p-3 text-sm">
      <h3 className="font-medium text-slate-800 dark:text-slate-100">{t('oneOff.payout')}</h3>
      <table className="w-full tabular-nums">
        <thead>
          <tr className="text-left text-xs text-slate-500 dark:text-slate-400">
            <th className="py-1 font-normal" />
            <th className="py-1 text-right font-normal">{t('oneOff.days')}</th>
            <th className="py-1 text-right font-normal">{t('oneOff.dayValue')}</th>
            <th className="py-1 text-right font-normal">{t('oneOff.amount')}</th>
          </tr>
        </thead>
        <tbody>
          {row.payout_terms.map((term) => (
            <tr key={term.kind} className="border-t border-border-subtle">
              <td className="py-1 text-slate-700 dark:text-slate-200">{t(`oneOff.term.${term.kind}`, { defaultValue: term.kind })}</td>
              <td className="py-1 text-right">{term.days ?? '—'}</td>
              <td className="py-1 text-right">{term.day_value == null ? '—' : formatMoney(term.day_value)}</td>
              <td className="py-1 text-right">{term.amount == null ? '—' : formatMoney(term.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {row.payout_total != null && (
        <dl>
          <Line label={t('oneOff.payoutTotal')} value={formatMoney(row.payout_total)} strong />
        </dl>
      )}
    </section>
  )
}

type DialogMode = { kind: 'hr'; mayPay: boolean; isSelf: boolean; onEdit: (row: OneOff) => void } | { kind: 'mine' }

/** One one-off payslip, relabelled by its own en/mn toggle without changing the app's language. */
function OneOffDialog({ row, mode, onClose }: { row: OneOff; mode: DialogMode; onClose: () => void }) {
  const { i18n } = useTranslation()
  const [lang, setLang] = useState<PayslipLang>(() => defaultPayslipLang(i18n.resolvedLanguage ?? i18n.language))
  const slipI18n = useMemo(() => i18n.cloneInstance({ lng: lang }), [i18n, lang])
  return (
    <I18nextProvider i18n={slipI18n}>
      <OneOffDialogBody row={row} mode={mode} lang={lang} onLang={setLang} onClose={onClose} />
    </I18nextProvider>
  )
}

function OneOffDialogBody({
  row,
  mode,
  lang,
  onLang,
  onClose,
}: {
  row: OneOff
  mode: DialogMode
  lang: PayslipLang
  onLang: (lang: PayslipLang) => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { confirm, confirmDelete, dialog } = useConfirm()
  const issue = useIssueOneOff()
  const voidIt = useVoidOneOff()
  const mail = useMailOneOff()
  const [saving, setSaving] = useState(false)
  const [voiding, setVoiding] = useState(false)
  const [voidReason, setVoidReason] = useState('')
  const [reissue, setReissue] = useState(false)
  const mustReissue = voidMustReissue(row)
  const actions =
    mode.kind === 'hr'
      ? oneOffActions(row.status, { ...mode, leaverReissue: row.resignation_payout_id != null && row.supersedes_id != null })
      : ['pdf' as const]
  const busy = issue.isPending || voidIt.isPending || mail.isPending

  async function downloadPdf() {
    setSaving(true)
    try {
      await downloadFile(
        mode.kind === 'mine' ? myOneOffPdfUrl(row.id, lang) : oneOffPdfUrl(row.id, lang),
        `one-off-payslip-${row.pay_date}.pdf`,
        lang,
      )
    } catch (e) {
      toast.error(`${t('oneOff.pdfFailed')} ${errorMessage(e)}`)
    } finally {
      setSaving(false)
    }
  }

  async function doIssue() {
    const text = row.resignation_payout_id
      ? t('oneOff.issueLeaverConfirm', { name: row.employee_name })
      : t('oneOff.issueConfirm', { name: row.employee_name, net: formatMoney(row.net) })
    if (!(await confirm(text, t('oneOff.issue')))) return
    try {
      await issue.mutateAsync(row.id)
      toast.success(t('oneOff.issued'))
      onClose()
    } catch (e) {
      toast.error(errorMessage(e))
    }
  }

  async function doDiscard() {
    if (!(await confirmDelete(t('oneOff.discardConfirm'), { detail: row.employee_name, okLabel: t('oneOff.discard') }))) return
    try {
      await voidIt.mutateAsync({ id: row.id, reason: t('oneOff.discard'), reissue: false })
      toast.success(t('oneOff.discarded'))
      onClose()
    } catch (e) {
      toast.error(errorMessage(e))
    }
  }

  async function doVoid() {
    try {
      await voidIt.mutateAsync({ id: row.id, reason: voidReason.trim(), reissue: reissue || mustReissue })
      toast.success(t('oneOff.voided'))
      onClose()
    } catch (e) {
      toast.error(errorMessage(e))
    }
  }

  async function doMail() {
    try {
      const outcome = await mail.mutateAsync(row.id)
      const notice = sendNotice(outcome, t)
      toast[notice.tone](notice.text)
    } catch (e) {
      toast.error(errorMessage(e))
    }
  }

  return (
    <ModalBackdrop onClose={onClose} aria-label={t('oneOff.title')}>
      <div className={dialogScrollPanelClass('lg')}>
        <div className="grid shrink-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 gap-y-2 px-5 pb-3 pt-5 sm:flex">
          <div className="min-w-0 sm:flex-1">
            <h2 className="font-semibold text-slate-900 dark:text-slate-100">
              {mode.kind === 'hr' ? row.employee_name : t('oneOff.title')}
            </h2>
            <p className={mutedTextClass}>
              {row.pay_date} · {t('oneOff.col.month')} {monthText(row.year, row.month)}
            </p>
          </div>
          <div className="col-span-2 row-start-2 sm:contents">
            <PayslipLangPicker lang={lang} onLang={onLang} />
          </div>
          <IconButton variant="secondary" label={t('oneOff.close')} icon={<X className="h-4 w-4" />} onClick={onClose} />
        </div>

        <div className={dialogScrollBodyClass}>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={STATUS_TONE[row.status]}>{t(`oneOff.status.${row.status}`)}</Badge>
            {row.reason && <span className="text-sm text-slate-600 dark:text-slate-300">{row.reason}</span>}
          </div>
          {row.status === 'Draft' && <Notice tone="info">{t('oneOff.draftNote')}</Notice>}
          {row.status === 'Void' && <Notice tone="info">{t('oneOff.voidNote', { reason: row.void_reason })}</Notice>}
          <OneOffWorkingView working={row} />
          <PayoutTerms row={row} />
          {voiding && (
            <div className="space-y-2 rounded-lg border border-border-subtle p-3">
              <TextArea label={t('oneOff.voidReason')} value={voidReason} onChange={(e) => setVoidReason(e.target.value)} rows={2} required />
              <Checkbox label={t('oneOff.voidReissue')} checked={reissue || mustReissue} disabled={mustReissue} onChange={setReissue} />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setVoiding(false)}>
                  {t('oneOff.cancel')}
                </Button>
                <Button variant="danger" disabled={!voidReason.trim()} loading={voidIt.isPending} onClick={() => void doVoid()}>
                  {t('oneOff.void')}
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className={`${dialogScrollFooterClass} flex-wrap gap-2`}>
          {actions.includes('discard') && (
            <Button variant="ghost" disabled={busy} icon={<Trash2 className="h-4 w-4" />} onClick={() => void doDiscard()}>
              {t('oneOff.discard')}
            </Button>
          )}
          {actions.includes('void') && !voiding && (
            <Button variant="ghost" disabled={busy} onClick={() => setVoiding(true)}>
              {t('oneOff.void')}
            </Button>
          )}
          <Button loading={saving} icon={<Download className="h-4 w-4" />} onClick={() => void downloadPdf()}>
            {t('oneOff.pdf')}
          </Button>
          {actions.includes('mail') && (
            <Button disabled={busy} loading={mail.isPending} icon={<Mail className="h-4 w-4" />} onClick={() => void doMail()}>
              {t('oneOff.mail')}
            </Button>
          )}
          {actions.includes('edit') && mode.kind === 'hr' && (
            <Button disabled={busy} onClick={() => mode.onEdit(row)}>
              {t('oneOff.edit')}
            </Button>
          )}
          {actions.includes('issue') && (
            <Button
              variant="primary"
              disabled={busy || row.warnings.length > 0}
              loading={issue.isPending}
              onClick={() => void doIssue()}
            >
              {t('oneOff.issue')}
            </Button>
          )}
        </div>
      </div>
      {dialog}
    </ModalBackdrop>
  )
}

/** New or saved Draft: the working beside the form is priced by the server as Issue would price it. */
function DraftDialog({ editing, onClose }: { editing: OneOff | null; onClose: () => void }) {
  const { t } = useTranslation()
  const { data: me } = useMe()
  const [draft, setDraft] = useState<OneOffDraft>(() => (editing ? draftOf(editing) : blankDraft(businessTodayIso())))
  const { data: people } = useDirectory({ limit: 1000 }, false)
  const create = useCreateOneOff()
  const edit = useEditOneOff()
  const ready = cleanDraft(draft)
  const preview = useOneOffPreview(ready && (editing ? { ...ready, id: editing.id } : ready))
  const problem = draftProblem(draft)
  const pending = create.isPending || edit.isPending

  const setLine = (index: number, patch: Partial<OneOffLine>) =>
    setDraft((was) => ({ ...was, lines: was.lines.map((line, i) => (i === index ? { ...line, ...patch } : line)) }))

  async function save() {
    if (!ready) return
    try {
      if (editing) await edit.mutateAsync({ ...ready, id: editing.id })
      else await create.mutateAsync(ready)
      toast.success(t('oneOff.saved'))
      onClose()
    } catch (e) {
      toast.error(errorMessage(e))
    }
  }

  return (
    <ModalBackdrop onClose={onClose} dismissOnBackdrop={false} aria-label={editing ? t('oneOff.edit') : t('oneOff.new')}>
      <div className={dialogScrollPanelClass('3xl')}>
        <div className="flex shrink-0 items-start justify-between gap-3 px-5 pb-3 pt-5">
          <h2 className="font-semibold text-slate-900 dark:text-slate-100">
            {editing ? `${t('oneOff.edit')}: ${editing.employee_name}` : t('oneOff.new')}
          </h2>
          <IconButton variant="secondary" label={t('oneOff.close')} icon={<X className="h-4 w-4" />} onClick={onClose} />
        </div>
        <div className={`${dialogScrollBodyClass} grid gap-5 md:grid-cols-[minmax(0,1fr)_18rem]`}>
          <div className="space-y-3">
            {editing ? (
              <p className="text-sm text-slate-700 dark:text-slate-200">{editing.employee_name}</p>
            ) : (
              <Select
                label={t('oneOff.person')}
                required
                value={draft.employee_id}
                onChange={(e) => setDraft((was) => ({ ...was, employee_id: e.target.value }))}
              >
                <option value="">{t('oneOff.pickPerson')}</option>
                {(people ?? [])
                  .filter((person) => person.id !== me?.id)
                  .map((person) => (
                    <option key={person.id} value={person.id}>
                      {person.display_name}
                    </option>
                  ))}
              </Select>
            )}
            <TextInput
              label={t('oneOff.payDate')}
              type="date"
              required
              value={draft.pay_date}
              onChange={(e) => setDraft((was) => ({ ...was, pay_date: e.target.value }))}
            />
            <fieldset className="space-y-2">
              <legend className="mb-1 text-sm text-slate-600 dark:text-slate-300">{t('oneOff.lines')}</legend>
              {draft.lines.map((line, index) =>
                line.kind === 'leave_payout' ? (
                  <div key={index} className="rounded-lg border border-border-subtle p-2 text-sm">
                    <div className="flex justify-between gap-3">
                      <span className="text-slate-700 dark:text-slate-200">{lineLabel(line, t)}</span>
                      <span className="tabular-nums text-slate-900 dark:text-slate-100">{formatMoney(line.amount)}</span>
                    </div>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t('oneOff.payoutLineFixed')}</p>
                  </div>
                ) : (
                  <div key={index} className="grid grid-cols-[8rem_minmax(0,1fr)_8rem_auto] items-end gap-2">
                    <Select
                      label={t('oneOff.lineKind')}
                      hideLabel={index > 0}
                      value={line.kind}
                      onChange={(e) => setLine(index, { kind: e.target.value as OneOffLine['kind'] })}
                    >
                      {MANUAL_KINDS.map((kind) => (
                        <option key={kind} value={kind}>
                          {t(`oneOff.kind.${kind}`)}
                        </option>
                      ))}
                    </Select>
                    <TextInput
                      label={t('oneOff.lineLabel')}
                      hideLabel={index > 0}
                      value={line.label}
                      maxLength={200}
                      onChange={(e) => setLine(index, { label: e.target.value })}
                    />
                    <TextInput
                      label={t('oneOff.lineAmount')}
                      hideLabel={index > 0}
                      inputMode="decimal"
                      inputClassName="text-right tabular-nums"
                      value={line.amount}
                      onChange={(e) => setLine(index, { amount: e.target.value })}
                    />
                    <IconButton
                      label={t('oneOff.removeLine')}
                      icon={<Trash2 className="h-4 w-4" />}
                      disabled={draft.lines.length === 1}
                      onClick={() => setDraft((was) => ({ ...was, lines: was.lines.filter((_, i) => i !== index) }))}
                    />
                  </div>
                ),
              )}
              <Button
                size="sm"
                variant="ghost"
                icon={<Plus className="h-3.5 w-3.5" />}
                onClick={() => setDraft((was) => ({ ...was, lines: [...was.lines, { kind: 'bonus', label: '', amount: '' }] }))}
              >
                {t('oneOff.addLine')}
              </Button>
            </fieldset>
            <TextArea
              label={t('oneOff.reason')}
              rows={2}
              maxLength={500}
              value={draft.reason}
              onChange={(e) => setDraft((was) => ({ ...was, reason: e.target.value }))}
            />
          </div>
          <section aria-live="polite" className="space-y-2 rounded-lg border border-border-subtle p-3">
            <h3 className="text-sm font-medium text-slate-800 dark:text-slate-100">{t('oneOff.working')}</h3>
            {problem ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">{t(`oneOff.problem.${problem}`)}</p>
            ) : preview.isError ? (
              <Notice tone="danger">{`${t('oneOff.previewFailed')} ${errorMessage(preview.error)}`}</Notice>
            ) : preview.data ? (
              <>
                <OneOffWorkingView working={preview.data} />
                <p className="text-xs text-slate-500 dark:text-slate-400">{t('oneOff.previewHint')}</p>
              </>
            ) : (
              <LoadingState rows={3} />
            )}
          </section>
        </div>
        <div className={dialogScrollFooterClass}>
          <Button variant="ghost" onClick={onClose}>
            {t('oneOff.cancel')}
          </Button>
          <Button variant="primary" disabled={!ready} loading={pending} onClick={() => void save()}>
            {t('oneOff.saveDraft')}
          </Button>
        </div>
      </div>
    </ModalBackdrop>
  )
}

function BankFileButton() {
  const { t } = useTranslation()
  const [month, setMonth] = useState(() => businessTodayIso().slice(0, 7))
  const [includeSent, setIncludeSent] = useState(false)
  const [busy, setBusy] = useState(false)

  async function run() {
    const [year, number] = month.split('-').map(Number)
    setBusy(true)
    try {
      const bank = await oneOffBankExport(year, number, includeSent)
      if (!bank.rows.length && !bank.withheld.length) {
        toast.info(includeSent ? t('oneOff.bankNone') : t('oneOff.bankNoneUnsent'))
        return
      }
      if (bank.rows.length) downloadWorkbook([bankSheet(bank.tsv, t('oneOff.bankFile'))], `one-off-bank-${month}.xlsx`)
      if (bank.withheld.length)
        toast.warning(
          t('oneOff.bankWithheld', {
            count: bank.withheld.length,
            names: bank.withheld.slice(0, 5).map((w) => `${w.name} (${w.reason})`).join(', '),
          }),
          { duration: 15_000 },
        )
    } catch (e) {
      toast.error(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-wrap items-end gap-2" title={t('oneOff.bankFileFor', { month })}>
      <TextInput label={t('oneOff.col.month')} hideLabel type="month" inputClassName="h-9 py-0" value={month} onChange={(e) => setMonth(e.target.value)} />
      <Checkbox className="h-9" label={t('oneOff.bankIncludeSent')} checked={includeSent} onChange={setIncludeSent} />
      <Button loading={busy} disabled={!/^\d{4}-\d{2}$/.test(month)} icon={<Landmark className="h-4 w-4" />} onClick={() => void run()}>
        {t('oneOff.bankFile')}
      </Button>
    </div>
  )
}

/** Payroll's One-off tab: the year's one-offs, each opened to read, issue, mail or void it. */
export default function OneOffPayslips() {
  const { t } = useTranslation()
  const { data: me } = useMe()
  const thisYear = Number(businessTodayIso().slice(0, 4))
  const [year, setYear] = useState(thisYear)
  const { data, isLoading, isError, error, refetch, isFetching } = useOneOffs(year)
  const [openId, setOpenId] = useState<string | null>(null)
  const [drafting, setDrafting] = useState<{ editing: OneOff | null } | null>(null)
  const mayPay = can(me, 'payroll')
  const rows = data ?? []
  const open = rows.find((row) => row.id === openId) ?? null

  const columns: ResponsiveTableColumn<OneOff>[] = [
    { key: 'person', header: t('oneOff.col.person'), cell: (row) => row.employee_name },
    { key: 'payDate', header: t('oneOff.col.payDate'), cell: (row) => row.pay_date },
    { key: 'month', header: t('oneOff.col.month'), cell: (row) => monthText(row.year, row.month) },
    {
      key: 'status',
      header: t('oneOff.col.status'),
      cell: (row) => <Badge tone={STATUS_TONE[row.status]}>{t(`oneOff.status.${row.status}`)}</Badge>,
    },
    { key: 'gross', header: t('oneOff.col.gross'), align: 'right', cell: (row) => <span className="tabular-nums">{formatMoney(row.gross)}</span> },
    { key: 'net', header: t('oneOff.col.net'), align: 'right', cell: (row) => <span className="tabular-nums">{formatMoney(row.net)}</span> },
    {
      key: 'bank',
      header: t('oneOff.col.bank'),
      hideOnCard: true,
      cell: (row) =>
        row.status !== 'Issued' ? null : (
          <span className="text-xs text-slate-500 dark:text-slate-400">{row.bank_exported_at ? t('oneOff.bankSent') : t('oneOff.notSent')}</span>
        ),
    },
  ]

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <Select label={t('oneOff.year')} hideLabel inputClassName="h-9 py-0" className="w-fit" value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {[thisYear + 1, thisYear, thisYear - 1, thisYear - 2].map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Select>
        {mayPay && (
          <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setDrafting({ editing: null })}>
            {t('oneOff.new')}
          </Button>
        )}
        <div className="ml-auto">{mayPay && <BankFileButton />}</div>
      </div>
      <p className="max-w-3xl text-sm text-slate-600 dark:text-slate-300">{t('oneOff.hint')}</p>
      {isError && <ErrorState title={t('oneOff.loadFailed')} error={error} onRetry={() => void refetch()} retrying={isFetching} />}
      {isLoading && <LoadingState rows={4} />}
      {!isLoading && !isError && rows.length === 0 && <EmptyState title={t('oneOff.empty')} />}
      {rows.length > 0 && (
        <div className="list-viewport list-sticky-head">
          <ResponsiveTable columns={columns} rows={rows} rowKey={(row) => row.id} onRowClick={(row) => setOpenId(row.id)} />
        </div>
      )}
      {open && (
        <OneOffDialog
          key={`${open.id}:${open.status}`}
          row={open}
          mode={{
            kind: 'hr',
            mayPay,
            isSelf: open.employee_id === me?.id,
            onEdit: (row) => {
              setOpenId(null)
              setDrafting({ editing: row })
            },
          }}
          onClose={() => setOpenId(null)}
        />
      )}
      {drafting && <DraftDialog key={drafting.editing?.id ?? 'new'} editing={drafting.editing} onClose={() => setDrafting(null)} />}
    </div>
  )
}

function MyOneOffOpen({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation()
  const { data, isError, error } = useMyOneOff(id)
  useEffect(() => {
    if (!isError) return
    toast.error(`${t('oneOff.loadFailed')} ${errorMessage(error)}`)
    onClose()
  }, [isError, error, onClose, t])
  return data ? <OneOffDialog row={data} mode={{ kind: 'mine' }} onClose={onClose} /> : null
}

/** My payslips' one-off section: the person's own Issued and Void one-offs in the picked period, masked until shown. */
export function MyOneOffPayslips({ period, shown }: { period: { start: string; end: string } | null; shown: boolean }) {
  const { t } = useTranslation()
  const { data, isLoading } = useMyOneOffs(period)
  const [openId, setOpenId] = useState<string | null>(null)
  if (!period || isLoading) return null
  const rows = data ?? []

  const columns: ResponsiveTableColumn<MyOneOff>[] = [
    { key: 'payDate', header: t('oneOff.col.payDate'), cell: (row) => row.pay_date },
    { key: 'month', header: t('oneOff.col.month'), cell: (row) => monthText(row.year, row.month) },
    {
      key: 'status',
      header: t('oneOff.col.status'),
      cell: (row) => <Badge tone={STATUS_TONE[row.status]}>{t(`oneOff.status.${row.status}`)}</Badge>,
    },
    {
      key: 'net',
      header: t('oneOff.col.net'),
      align: 'right',
      cell: (row) =>
        shown ? (
          <button
            type="button"
            onClick={() => setOpenId(row.id)}
            className="inline-flex items-center gap-1 tabular-nums text-blue-700 underline-offset-2 hover:underline dark:text-blue-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
          >
            <FileText className="h-3.5 w-3.5" aria-hidden="true" />
            {formatMoney(row.net)}
          </button>
        ) : (
          <span className="tabular-nums text-slate-500 dark:text-slate-400">{masked(false, '')}</span>
        ),
    },
  ]

  return (
    <section className="space-y-2">
      <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">{t('oneOff.mine')}</h2>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">{t('oneOff.mineEmpty')}</p>
      ) : (
        <ResponsiveTable columns={columns} rows={rows} rowKey={(row) => row.id} />
      )}
      {openId && shown && <MyOneOffOpen id={openId} onClose={() => setOpenId(null)} />}
    </section>
  )
}
